/**
 * SectionProgress - Visual progress indicator for questionnaire sections
 */

import type { QuestionSection } from '@/types'
import { SECTION_INFO, SECTION_ORDER } from '@/types/questionnaire.types'
import * as LucideIcons from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Check } from 'lucide-react'

interface SectionProgressProps {
  currentSection: QuestionSection
  completedSections: QuestionSection[]
  onSectionClick?: (section: QuestionSection) => void
  className?: string
}

export function SectionProgress({
  currentSection,
  completedSections,
  onSectionClick,
  className,
}: SectionProgressProps) {
  return (
    <nav className={cn('flex flex-wrap gap-2', className)}>
      {SECTION_ORDER.map((section, index) => {
        const info = SECTION_INFO[section]
        const isCompleted = completedSections.includes(section)
        const isCurrent = section === currentSection
        const isPast = index < SECTION_ORDER.indexOf(currentSection)
        const canClick = onSectionClick && (isCompleted || isPast || isCurrent)
        
        const IconComponent = (LucideIcons as unknown as Record<string, LucideIcon>)[info.icon]

        return (
          <button
            key={section}
            type="button"
            disabled={!canClick}
            onClick={() => canClick && onSectionClick?.(section)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg transition-all',
              'text-sm font-medium',
              isCurrent && 'bg-primary text-primary-foreground shadow-sm',
              isCompleted && !isCurrent && 'bg-primary/10 text-primary',
              !isCompleted && !isCurrent && 'bg-muted text-muted-foreground',
              canClick && 'cursor-pointer hover:opacity-80',
              !canClick && 'cursor-not-allowed opacity-50'
            )}
          >
            {isCompleted && !isCurrent ? (
              <Check className="h-4 w-4" />
            ) : IconComponent ? (
              <IconComponent className="h-4 w-4" />
            ) : null}
            <span className="hidden sm:inline">{info.title}</span>
            <span className="sm:hidden">{index + 1}</span>
          </button>
        )
      })}
    </nav>
  )
}

// Alternative vertical progress for sidebar
export function SectionProgressVertical({
  currentSection,
  completedSections,
  onSectionClick,
  className,
}: SectionProgressProps) {
  return (
    <nav className={cn('space-y-1', className)}>
      {SECTION_ORDER.map((section, index) => {
        const info = SECTION_INFO[section]
        const isCompleted = completedSections.includes(section)
        const isCurrent = section === currentSection
        const isPast = index < SECTION_ORDER.indexOf(currentSection)
        const canClick = onSectionClick && (isCompleted || isPast || isCurrent)
        
        return (
          <button
            key={section}
            type="button"
            disabled={!canClick}
            onClick={() => canClick && onSectionClick?.(section)}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left',
              isCurrent && 'bg-primary text-primary-foreground shadow-sm',
              isCompleted && !isCurrent && 'bg-primary/10 text-primary hover:bg-primary/20',
              !isCompleted && !isCurrent && 'bg-muted/50 text-muted-foreground',
              canClick && 'cursor-pointer',
              !canClick && 'cursor-not-allowed'
            )}
          >
            {/* Step indicator */}
            <div className={cn(
              'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
              isCurrent && 'bg-primary-foreground/20',
              isCompleted && !isCurrent && 'bg-primary/20',
              !isCompleted && !isCurrent && 'bg-muted'
            )}>
              {isCompleted && !isCurrent ? (
                <Check className="h-4 w-4" />
              ) : (
                index + 1
              )}
            </div>
            
            {/* Section info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{info.title}</div>
              <div className={cn(
                'text-xs truncate',
                isCurrent && 'text-primary-foreground/80',
                !isCurrent && 'text-muted-foreground'
              )}>
                ~{info.estimatedMinutes} min
              </div>
            </div>
          </button>
        )
      })}
    </nav>
  )
}
